import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { mentionBoostEligibilityQueryKey } from '@/hooks/queries/use-mention-boost-eligibility-query';
import {
  type ApiErrorResponse,
  type ApiResponse,
  type RequestMentionBoostAuthorizationRequest,
  type RequestMentionBoostAuthorizationResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface RequestMentionBoostAuthorizationVariables
  extends RequestMentionBoostAuthorizationRequest {
  mentionId: string;
}

const requestMentionBoostAuthorization = async ({
  mentionId,
  scope,
}: RequestMentionBoostAuthorizationVariables): Promise<
  ApiResponse<RequestMentionBoostAuthorizationResponse>
> => {
  const response = await api.post<ApiResponse<RequestMentionBoostAuthorizationResponse>>(
    `/dashboard/mentions/${mentionId}/boost-authorization`,
    { scope }
  );

  return response.data;
};

export const useRequestMentionBoostAuthorization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<RequestMentionBoostAuthorizationResponse>,
    AxiosError<ApiErrorResponse>,
    RequestMentionBoostAuthorizationVariables
  >({
    mutationFn: requestMentionBoostAuthorization,
    onSuccess: (response, variables) => {
      toaster.success({
        title: t('mentions.boost.confirm.requestSuccess'),
        description: response.data?.authorization.requestedAt
          ? t('mentions.boost.confirm.requestSuccessDescription')
          : undefined,
      });

      queryClient.setQueryData(
        mentionBoostEligibilityQueryKey(variables.mentionId),
        response.data?.eligibility
      );
      queryClient.invalidateQueries({
        queryKey: mentionBoostEligibilityQueryKey(variables.mentionId),
      });
    },
    onError: (error) => {
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'common.error'),
      });
    },
  });
};
